'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { RecoveryType } from '@prisma/client';
import { useTranslations } from 'next-intl';
import { useAction } from 'next-safe-action/hooks';
import Image from 'next/image';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { commonActionErrorHandler } from '~/lib/action-error-handler';
import { onSuccess } from '~/lib/action-wrappers';
import { cn } from '~/lib/utils';
import { CircularProgress } from '~/components/ui-custom/circular-progress';
import { DialogCloseButton, DialogCreateButton } from '~/components/ui-custom/dialog-actions';
import { FormSimpleInput } from '~/components/ui-custom/form-simple-input';
import { Button } from '~/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog';
import { Form, FormField } from '~/components/ui/form';
import { actionSendRecoveryRequest } from '~/modules/recovery/actions/action-recover-request-send-email';
const resetPinFormSchema = z.object({
  email: z.string().min(8, 'Field is required.').email('Email is invalid.')
});
type ResetPinFormSchema = z.input<typeof resetPinFormSchema>;
export const ResetPasswordPinDialog = ({
  type,
  onClose,
  onSubmit
}: {
  onClose: () => void;
  type: 'password' | 'pin';
  onSubmit: (values: ResetPinFormSchema, onSuccess: () => void) => void;
}) => {
  const t = useTranslations('ResetPasswordPinDialog');
  const [emailSent, setEmailSent] = useState(false);
  const $actionSendRecoveryRequest = useAction(actionSendRecoveryRequest, {
    onSuccess: onSuccess(result => {
      if (result.sent) {
        setEmailSent(true);
      } else {
        toast.error(result.message);
      }
    }),
    onError: commonActionErrorHandler
  });
  const form = useForm<ResetPinFormSchema>({
    resolver: zodResolver(resetPinFormSchema),
    defaultValues: {
      email: ''
    }
  });
  const isResetPinCode = type === 'pin';
  const email = form.watch('email');
  return <Dialog open onOpenChange={open => {
    if (!open) {
      onClose();
    }
  }} data-sentry-element="Dialog" data-sentry-component="ResetPasswordPinDialog" data-sentry-source-file="reset-password-pin-dialog.tsx">
      <DialogContent className="max-h-screen overflow-y-auto max-w-80 md:max-w-lg" noCloseButton data-sentry-element="DialogContent" data-sentry-source-file="reset-password-pin-dialog.tsx">
        <DialogHeader className="py-4" data-sentry-element="DialogHeader" data-sentry-source-file="reset-password-pin-dialog.tsx">
          <div className="grid grid-cols-5 gap-1 items-center justify-between">
            <Image className="col-span-1" src="/img/logo.png" alt="KA Connect logo" width={48} height={48} data-sentry-element="Image" data-sentry-source-file="reset-password-pin-dialog.tsx" />
            <DialogTitle className="col-span-3 text-primary text-base md:text-lg text-center" data-sentry-element="DialogTitle" data-sentry-source-file="reset-password-pin-dialog.tsx">
              {isResetPinCode ? t('reset_pin') : t('reset_password')}
            </DialogTitle>
            <CircularProgress value={emailSent ? 2 : 1} total={2} data-sentry-element="CircularProgress" data-sentry-source-file="reset-password-pin-dialog.tsx" />
          </div>
        </DialogHeader>
        {emailSent ? <>
            <div className="flex flex-col gap-y-2">
              <span className="text-base font-semibold">{t('email_sent')}</span>
              {t('email_sent_subtitle', {
            type: isResetPinCode ? t('pin') : t('password')
          }).split('\n').map((str, idx) => <p key={idx} className="text-sm font-normal text-[#7B7A7B]">
                    {str}
                  </p>)}
              <p className="text-sm font-normal text-[#7B7A7B]">{t('problem_persist')}</p>
            </div>
            <DialogFooter className={cn(!emailSent && 'mt-8')}>
              <div className="flex w-full justify-center">
                <Button onClick={onClose}>{t('okay')}</Button>
              </div>
            </DialogFooter>
          </> : <Form {...form}>
            <form autoComplete="off" onSubmit={form.handleSubmit(values => {
          onSubmit(values, () => $actionSendRecoveryRequest.execute({
            email,
            type: isResetPinCode ? RecoveryType.PIN : RecoveryType.PASSWORD
          }));
        })}>
              <FormField name="email" control={form.control} render={({
            field
          }) => <FormSimpleInput {...field} isRequired autoComplete="off" label={t('email')} />} />
              <DialogFooter className="mt-8">
                <DialogCloseButton disabled={false} />
                <DialogCreateButton label={t('send_email')} isLoading={$actionSendRecoveryRequest.isPending} />
              </DialogFooter>
            </form>
          </Form>}
      </DialogContent>
    </Dialog>;
};